import { Investors } from '@components/book-trial-class-jp/investors'
import CodingForStudents from '@components/common/coding-for-students'
import Footer from '@components/common/footer'
import Navbar from '@components/common/navbar'
import Activities from '@components/course-landing-page/activities'
import ClassCurriculum from '@components/course-landing-page/class-curriculum'
import CourseDescription from '@components/course-landing-page/course-description'
import CourseFeatures from '@components/course-landing-page/course-features'
import Faq from '@components/course-landing-page/faq'
import FirstFold from '@components/course-landing-page/first-fold'
import FooterBanner from '@components/course-landing-page/footer-banner'
import RecommendedCourses from '@components/course-landing-page/recommended-courses'
import StemAccredited from '@components/course-landing-page/stem-accredited'
import Teachers from '@components/course-landing-page/teachers'
import { COURSE_INFO } from '@components/locale-pages/home/courses'
import VideoTestimonial from '@components/locale-pages/home/video-testimonial'
import useStoreUTMParams from '@hooks/useStoreUTMParam'
import { COURSE_NAMES, FAQ } from '@lib/data/course-landing-page'
import { VIDEO_TESTIMONIAL } from '@lib/data/summer-coding-camp'
import { formatSeoConfig } from '@lib/seo'
import { Trans } from '@lingui/macro'
import { CourseJsonLd, FAQPageJsonLd, NextSeo } from 'next-seo'
import dynamic from 'next/dynamic'
import { useState } from 'react'
import CodingalProductBlog from '@components/blog/product-blogs'
import TrustpilotReview from '@components/locale-pages/home/trustpilot-review'
import ContentCard from '@components/course-landing-page/content-card'
import { FastlyImage } from '../../components/common/image'
import CodingCertificate from '../../components/common/coding-certificate'

const CourseCard = dynamic(() =>
  import('@components/platform-course-landing-page/course-card')
)

const { PYGAME } = COURSE_NAMES
const urlQuery = `?course=${COURSE_INFO['pc'].trialCourseQuery}`

export const PygameSeo = formatSeoConfig({
  title: 'Pygame for Kids: Build Python Games Online',
  description:
    'Pygame classes for kids taught by expert instructors. Build interactive 2D games using Python with a structured curriculum at Codingal. Try a free lesson.',
  url: 'https://www.codingal.com/courses/pygame/',
  metaImage: 'https://fastly.codingal.com/images/courses/og/pygame.jpg',
})

const BegineerGuide = () => (
  <div className="space-y-3">
    <h3 className="font-600 text-lg lg:text-xl">
      Why Should Kids Learn Pygame?
    </h3>
    <p>Learning Pygame is a great way for kids to:</p>
    <ul>
      <li>Boost creativity by designing and coding their own games</li>
      <li>
        Build problem-solving skills through logic, movement, and scoring
        challenges
      </li>
      <li>
        Understand the basics of game development, a stepping stone to future
        coding careers
      </li>
      <li>Express themselves by turning ideas into interactive projects</li>
      <li>
        Gain a strong foundation for learning advanced coding languages and game
        engines
      </li>
    </ul>
    <p>
      Pygame helps kids see coding as fun and rewarding while preparing them for
      real-world programming.
    </p>

    <h3 className="font-600 text-lg lg:text-xl">
      How Do You Explain Pygame to a Child?
    </h3>
    <p>
      Pygame is like a playground for coding where kids can make their own games
      using Python. It lets them create characters, move objects, add sounds,
      and build fun projects like Snake or Pong. Think of it as digital LEGO,
      where instead of building with blocks, kids build with code to turn their
      ideas into games.
    </p>
    <h3 className="font-600 text-lg lg:text-xl">What Does Pygame Stand For?</h3>
    <div>
      <FastlyImage
        src={`/images/courses/pygame/pygame-1.jpg`}
        alt='Infographic titled "What does Pygame stands for?"'
        width={512}
        height={341}
      />
    </div>
    <p>
      Pygame is a special tool that helps kids create their own video games
      using Python. It lets you add characters, sounds, and movements to make
      games fun and interactive. With Pygame, kids can turn their ideas into
      real playable games on the computer!
    </p>
    <h3 className="font-600 text-lg lg:text-xl">
      An Easy Way to Understand Pygame for Kids
    </h3>
    <p>
      Here is a simple analogy to help you understand Pygame. Imagine you are
      setting up a stage for a play. First, you place the background, maybe a
      city skyline or a forest. Then, you bring in the characters like
      superheroes, animals, or robots. Finally, you add actions like running,
      jumping, or flying.
    </p>
    <p>
      That stage setup is your Pygame. It provides the tools to create the
      background, add characters (called sprites), and bring them to life with
      movements and interactions. Without Pygame, the story just stays in your
      head. With Pygame, it becomes a playable game!
    </p>
    <p>
      This is exactly how kids learn Pygame in our kids&apos; Pygame classes
      taking their imagination, adding structure, and turning it into real
      interactive games. From a bouncing ball to a superhero adventure, Pygame
      makes their ideas come alive on screen!
    </p>

    <h3 className="font-600 text-lg lg:text-xl">
      What Kids Can Learn with Pygame
    </h3>
    <div>
      <FastlyImage
        src={`/images/courses/pygame/pygame-2.jpg`}
        alt='Infographic titled "What Kids Can Learn with Pygame"'
        width={512}
        height={341}
      />
    </div>
    <p>
      When kids learn Pygame, they are not just playing games, they are creating
      them! Pygame teaches children how to think like game designers and problem
      solvers. It gives them the skills to plan, organize, and create projects
      while having fun with coding.
    </p>
    <p>Here&apos;s what kids can learn with Pygame:</p>
    <ol>
      <li>
        <strong>Game Design Basics</strong> – How to create characters,
        backgrounds, and levels that tell a story.
      </li>
      <li>
        <strong>Coding Logic</strong> – Using Python to make things move, jump,
        collide, or respond to player actions.
      </li>
      <li>
        <strong>Creativity & Storytelling</strong> – Designing their own
        adventures, challenges, and rules for the game.
      </li>
      <li>
        <strong>Problem-Solving Skills</strong> – Fixing bugs, testing their
        code, and learning how to improve their projects.
      </li>
      <li>
        <strong>Interactive Projects</strong> – Building complete games they can
        play and share with friends and family.
      </li>
    </ol>
    <p>
      In short, Pygame turns kids into digital creators by helping them combine
      coding, art, and imagination to make something uniquely their own.
    </p>
  </div>
)

const Pygame = () => {
  const [shouldUpdateCourseCard, setShouldUpdateCourseCard] = useState(false)

  const handleHideCourseCard = (value: boolean) => {
    setShouldUpdateCourseCard(value)
  }
  useStoreUTMParams()

  return (
    <>
      <NextSeo {...PygameSeo} />
      <FAQPageJsonLd
        mainEntity={FAQ[PYGAME].map(({ acceptedAnswerText, questionName }) => ({
          acceptedAnswerText: acceptedAnswerText,
          questionName: questionName,
        }))}
      />
      <CourseJsonLd
        courseName="Learn Pygame with Top AI & Coding Course Instructors for Kids and Teens"
        description="Empower your child to move from consuming content to creating it. Codingal’s website development course teaches Pygame to kids and teens the building blocks of the web, enabling them to design and structure their own websites. With guidance from expert instructors, students will embark on a journey that combines creativity with foundational coding skills, setting the stage for future exploration in AI & coding."
        provider={{
          '@type': 'Organization',
          name: 'Codingal',
        }}
        offers={{
          '@type': 'Offer',
          category: 'Paid',
        }}
        hasCourseInstance={{
          '@type': 'CourseInstance',
          courseMode: 'Online',
          courseWorkload: 'PT44H',
        }}
      />
      <Navbar tryFreeClassLink={`/register/${urlQuery}`} />
      <FirstFold
        getShouldHideCourseCard={handleHideCourseCard}
        courseName={PYGAME}
        urlQuery={urlQuery}
        isPlatformPage
      />
      <div className="container flex justify-between pt-10 mx-auto px-5 lg:px-10 text-grey">
        <div className="pb-10 md:w-4/5 md:pr-4 max-w-3xl">
          <CourseDescription courseName={PYGAME} />
          <CourseFeatures
            courseTitle={PYGAME}
            title={<Trans>Engaging Game Development classes for kids</Trans>}
          />
          <ClassCurriculum
            courseName={PYGAME}
            title="Our Pygame class curriculum"
            description={
              <Trans>
                Our Pygame curriculum is designed to guide students through
                every step of game development using Python. Starting with the
                basics, students will learn to set up their development
                environment, create interactive game screens, and work with
                visual elements like images, backgrounds, and text. As the
                course progresses, they will dive into more complex topics like
                adding sprites, collision detection, and custom events, building
                up to creating their own full-fledged games.
                <br />
                With a hands-on approach, students will apply their skills to
                create engaging games like Space Invader, learning essential
                game-building concepts along the way. By the end of the course,
                students will have developed problem-solving skills, mastered
                key game development techniques, and gained the confidence to
                bring their own creative ideas to life using Python & Pygame.
              </Trans>
            }
          />
          <Activities courseName={PYGAME} />
          <Teachers
            urlQuery={urlQuery}
            title={
              <Trans>
                Learn from experienced teachers. Our hand-picked AI & coding
                tutors ensure the best learning experience
              </Trans>
            }
            description={
              <Trans>
                Our instructors bring deep expertise in Computer Science and
                complete rigorous training before they teach. They’re truly
                amazing, and it shows in our exceptional Net Promoter Score
                (NPS) of 86! Students and parents consistently rate our teachers
                4.9 out of 5, and our curriculum earns a fantastic 4.8 out of 5.
                We’re so proud to have a faculty that’s 91% female and 9% male,
                creating an inclusive, encouraging learning environment where
                everyone feels welcome. Each tutor is trained to understand
                individual student needs and deliver personalized support, even
                in group settings. Plus, students have 24/7 access to expert
                tutors for instant doubt resolution, anytime they need help.
                This makes learning Pygame a truly supported experience,
                especially through our virtual Pygame lessons that connect kids
                with top educators worldwide.
              </Trans>
            }
          />
          <ContentCard
            title={
              <Trans>
                The best place for kids to learn the basics and fundamentals of
                Game Development with Pygame
              </Trans>
            }
            description={
              <Trans>
                <span className="block">
                  Codingal offers a nurturing environment where middle and high
                  school learners can explore the world of game development with
                  Pygame without fear. Our structured curriculum, combined with
                  expert instructors, ensures that students build a strong
                  foundation in coding, preparing them for exciting future
                  endeavors in AI, game design, and programming.
                </span>
              </Trans>
            }
            courseName={PYGAME}
          />
          <StemAccredited
            urlQuery={urlQuery}
            heading="STEM.org accredited educational experience and certificates"
            description={
              <>
                At Codingal, your child masters Pygame through a STEM.org -
                accredited program built on proven educational frameworks like
                Bloom’s Taxonomy, BIDE, and STEAM. Our curriculum is aligned
                with Common Core, state standards, AP Computer Science goals,
                and the K - 12 Computer Science Framework - ensuring learning
                that’s both age-appropriate and college-ready. Through hands-on,
                project-based learning, students create real-world apps like
                temperature converters and scientific calculators, applying
                math, science, and engineering logic to solve everyday problems.
                Backed by STEM.org’s rigorous audits, every Pygame lesson lays a
                strong, future-proof foundation in STEM.
              </>
            }
          />
          <CodingCertificate
            title="Unlock your skills and earn your Pygame certifications"
            description="In the Website Development track, students in grades 6 - 12 begin their journey with Pygame, the foundational language of the web. They learn how to structure web pages using elements like headings, paragraphs, links, images, lists, tables, and forms. As they progress, students apply semantic Pygame to improve accessibility and build pages that mirror real-world websites. Through hands-on lessons and creative projects, they transform code into fully functional web pages. By the end of the Pygame module, students not only understand how websites are built but also develop a strong foundation for diving into web design, interactivity with JavaScript, and more advanced web technologies."
            imagePath="/images/courses/ai-and-ml/cetrificate"
            imageWidth={230}
            imageHeight={195}
          />
          <CodingForStudents
            title="Our Pygame classes are appropriate for middle school & high school students"
            description={
              <>
                At Codingal, our Pygame classes are designed especially for
                middle and high school students who are ready to move from
                simple coding to real game development.
                <br />
                Our foundational Pygame curriculum introduces students to Python
                game design in a fun, beginner-friendly way. They start with the
                basics, creating game windows, drawing shapes, and moving
                characters. As they progress, students learn how to add sprites,
                control movement with keyboard inputs, use sounds, and build
                levels with scoring and timers.
                <br />
                Step by step, they advance into more complex concepts like
                collision detection, animations, and designing full interactive
                games. With each project, students gain practical experience in
                game design, coding logic, and problem-solving , while creating
                their very own playable games.
                <br />
                By the end of the course, kids not only strengthen their Python
                skills but also gain the confidence to design and share games,
                preparing them for more advanced coding journeys and
                future-ready STEM skills.
              </>
            }
            imageWidth={235}
            imageHeight={241}
            imagePath="/images/courses/ai-and-ml/ai-course-students"
          />
          <VideoTestimonial
            isCoursepage
            title={
              <Trans>
                What students and parents have to say about our training program
              </Trans>
            }
            video_testimonial={VIDEO_TESTIMONIAL}
          />
          <TrustpilotReview isCoursepage urlQuery={urlQuery} />
          <Investors isCoursePage urlQuery={urlQuery} />
          <RecommendedCourses courseCodes={['wd', 'cgm']} />
          <ContentCard
            urlQuery={urlQuery}
            title={<>The Ultimate Guide to Pygame for kids</>}
            description={<BegineerGuide />}
          />
          <Faq
            courseName={PYGAME}
            title="Frequently Asked Questions: Pygame for Kids"
          />
          <CodingalProductBlog urlQuery={urlQuery} isCoursePage />
        </div>
        <div className="hidden ml-4 md:block w-80">
          <div className="pb-5 sticky top-0 pt-5 z-40 w-80">
            {shouldUpdateCourseCard && (
              <CourseCard
                shouldHideCardImage={true}
                courseName={PYGAME}
                urlQuery={urlQuery}
              />
            )}
          </div>
        </div>
      </div>
      <FooterBanner
        title="Pygame for kids with the best Computer Science teachers"
        url={`/register/${urlQuery}`}
      />
      <Footer />
    </>
  )
}

// TODO: Remove once getInitialProps is removed from _app.tsx
export const getStaticProps = async () => {
  return {
    props: {},
  }
}

export default Pygame
